import type { TFunction } from 'i18next';

import { MENU_OBJECT_TYPES, TYPE_TO_READABLE_STRING_MAP } from 'constants/menuManagement';
import { getReadableItemTypePlural } from 'helpers/menuManagement';
import {
  getMasterMenuDisabledTooltip,
  getCloneMenuDisabledTooltip,
  getPosSyncRowDisabledTooltip,
} from 'helpers/masterMenuPermissions';

type BadgeLabelKey = 'SubItem.badge.item' | 'SubItem.badge.group' | 'SubItem.badge.mod';

type BadgeTypeAndVariant = { labelKey: BadgeLabelKey; variant: string } | Record<string, never>;

/** Returns a translation key rather than copy so the caller owns the `t` lookup. */
export const getBadgeTypeAndVariant = (itemType: string): BadgeTypeAndVariant => {
  switch (itemType) {
    case MENU_OBJECT_TYPES.items:
      return { labelKey: 'SubItem.badge.item', variant: 'blue' };
    case MENU_OBJECT_TYPES.modifierGroups:
      return { labelKey: 'SubItem.badge.group', variant: 'pink' };
    case MENU_OBJECT_TYPES.modifiers:
      return { labelKey: 'SubItem.badge.mod', variant: 'yellow' };
    default:
      return {};
  }
};

type ServingsItem = { itemType: string; servingsTag?: string };

export const formatServingsTag = (
  item: ServingsItem,
  t: TFunction<'menuManagementSubItem'>,
): string => {
  if (item.itemType !== MENU_OBJECT_TYPES.items) return '';
  if (!item.servingsTag) return t('SubItem.servings.empty');
  return t('SubItem.servings.value', { servingsTag: item.servingsTag });
};

/** "cuboh_modifier_group" -> "modifier group", for copy that names the row's entity. */
export const getReadableRowEntity = (itemType: string): string =>
  TYPE_TO_READABLE_STRING_MAP[
    itemType as keyof typeof TYPE_TO_READABLE_STRING_MAP
  ]?.toLowerCase() ?? 'item';

type DisabledTooltipItem = { itemType: string; noEdit?: boolean };

export const getDisabledTooltip = ({
  item,
  hasLimitedAccess,
  isPosSyncInPlace = false,
}: {
  item: DisabledTooltipItem;
  hasLimitedAccess: boolean;
  isPosSyncInPlace?: boolean;
}): string | null => {
  if (hasLimitedAccess) return getMasterMenuDisabledTooltip();
  if (isPosSyncInPlace) return getPosSyncRowDisabledTooltip(getReadableRowEntity(item.itemType));
  if (item.noEdit) return getCloneMenuDisabledTooltip(getReadableItemTypePlural(item?.itemType));
  return null;
};
